// src/pages/customer-panel/courses/CoursesPage.js
// Course browsing page - Student / Customer view

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    Chip,
    TextField,
    InputAdornment,
    Pagination,
    Paper,
    CircularProgress,
    Alert,
    Snackbar,
    Skeleton,
    Fade,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Tooltip,
    Rating,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';

const DIFFICULTY_COLORS = {
    beginner: { bg: '#dcfce7', color: '#166534' },
    intermediate: { bg: '#fef3c7', color: '#92400e' },
    advanced: { bg: '#fee2e2', color: '#991b1b' },
};

const CATEGORY_LIST = [
    'All',
    'Development',
    'Design',
    'Marketing',
    'Business',
    'Data Science',
    'Photography',
    'Music',
    'Health',
];

const DIFFICULTY_LIST = ['all', 'beginner', 'intermediate', 'advanced'];
const PRICING_LIST = ['all', 'free', 'paid'];

// Gradient placeholders when no thumbnail
const CARD_GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
];

function getGradient(index) {
    return CARD_GRADIENTS[index % CARD_GRADIENTS.length];
}

const CourseCard = ({ course, index, onEnroll, enrollingId }) => {
    const navigate = useNavigate();
    const hasThumbnail = course.thumbnail || course.image;

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    borderColor: ACCENT,
                },
            }}
            onClick={() => navigate(`/customer-panel/courses/${course.slug || course.id || course._id}`)}
        >
            {hasThumbnail ? (
                <CardMedia
                    component="img"
                    height="180"
                    image={course.thumbnail || course.image}
                    alt={course.title}
                    sx={{ objectFit: 'cover' }}
                />
            ) : (
                <Box
                    sx={{
                        height: 180,
                        background: getGradient(index),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                    }}
                >
                    <SchoolIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.7)' }} />
                    <Tooltip title="Bookmark">
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.2)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.4)' },
                            }}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <BookmarkBorderIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                {/* Category & Difficulty */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    {course.category && (
                        <Chip
                            label={course.category}
                            size="small"
                            sx={{
                                fontFamily,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: `${ACCENT}15`,
                                color: ACCENT,
                                height: 24,
                            }}
                        />
                    )}
                    {course.difficulty && (
                        <Chip
                            label={course.difficulty.charAt(0).toUpperCase() + course.difficulty.slice(1)}
                            size="small"
                            sx={{
                                fontFamily,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: DIFFICULTY_COLORS[course.difficulty]?.bg || '#f1f5f9',
                                color: DIFFICULTY_COLORS[course.difficulty]?.color || '#475569',
                                height: 24,
                            }}
                        />
                    )}
                </Box>

                {/* Title */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        color: '#1e293b',
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                    }}
                >
                    {course.title}
                </Typography>

                {/* Description */}
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily,
                        color: '#64748b',
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                        fontSize: '0.8rem',
                    }}
                >
                    {course.description || 'No description available.'}
                </Typography>

                {/* Meta row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {(course.rating !== undefined && course.rating !== null) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Rating
                                value={Number(course.rating) || 0}
                                precision={0.5}
                                size="small"
                                readOnly
                                icon={<StarIcon fontSize="inherit" sx={{ color: '#f59e0b' }} />}
                                emptyIcon={<StarIcon fontSize="inherit" sx={{ color: '#e2e8f0' }} />}
                            />
                            <Typography variant="caption" sx={{ fontFamily, color: '#64748b', fontWeight: 600 }}>
                                {Number(course.rating).toFixed(1)}
                            </Typography>
                        </Box>
                    )}
                    {(course.enrolled_count !== undefined || course.students_count !== undefined) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PeopleIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                                {course.enrolled_count || course.students_count || 0} enrolled
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>

            <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0, justifyContent: 'space-between' }}>
                {/* Price */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        color: course.price && course.price > 0 ? '#1e293b' : ACCENT,
                    }}
                >
                    {!course.price || course.price === 0 ? 'Free' : `$${Number(course.price).toFixed(2)}`}
                </Typography>

                {course.is_enrolled ? (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customer-panel/courses/${course.slug || course.id || course._id}`);
                        }}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderColor: ACCENT,
                            color: ACCENT,
                            borderRadius: '10px',
                            '&:hover': { borderColor: '#059669', bgcolor: `${ACCENT}08` },
                        }}
                    >
                        Continue
                    </Button>
                ) : (
                    <Button
                        size="small"
                        variant="contained"
                        disabled={enrollingId === (course.id || course._id)}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEnroll(course.id || course._id);
                        }}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            bgcolor: ACCENT,
                            borderRadius: '10px',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#059669', boxShadow: 'none' },
                        }}
                    >
                        {enrollingId === (course.id || course._id) ? (
                            <CircularProgress size={18} sx={{ color: 'white' }} />
                        ) : (
                            'Enroll'
                        )}
                    </Button>
                )}
            </CardActions>
        </Card>
    );
};

const CourseCardSkeleton = () => (
    <Card sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Skeleton variant="rectangular" height={180} />
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <Skeleton variant="rounded" width={70} height={24} />
                <Skeleton variant="rounded" width={90} height={24} />
            </Box>
            <Skeleton variant="text" sx={{ fontSize: '1.1rem', mb: 0.5 }} />
            <Skeleton variant="text" sx={{ fontSize: '0.85rem' }} />
            <Skeleton variant="text" sx={{ fontSize: '0.85rem', width: '60%' }} />
        </CardContent>
    </Card>
);

export default function CoursesPage() {
    const navigate = useNavigate();

    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [category, setCategory] = useState('All');
    const [difficulty, setDifficulty] = useState('all');
    const [pricing, setPricing] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [enrollingId, setEnrollingId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [showFilters, setShowFilters] = useState(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(search);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchCourses = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = { page, per_page: 12 };
            if (searchDebounced) params.search = searchDebounced;
            if (category && category !== 'All') params.category = category;
            if (difficulty && difficulty !== 'all') params.difficulty = difficulty;
            if (pricing && pricing !== 'all') params.pricing = pricing;

            const response = await axiosInstance.get('/lms/portal/courses', { params });
            const data = response.data;
            setCourses(data.courses || data.data || data.results || []);
            setTotalPages(data.total_pages || data.totalPages || Math.ceil((data.total || 0) / 12) || 1);
        } catch (err) {
            console.error('Failed to fetch courses:', err);
            setError(err.response?.data?.message || 'Failed to load courses. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [searchDebounced, category, difficulty, pricing, page]);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    const handleEnroll = useCallback(async (courseId) => {
        setEnrollingId(courseId);
        try {
            await axiosInstance.post('/lms/portal/enroll', { course_id: courseId });
            setSnackbar({ open: true, message: 'Successfully enrolled in the course!', severity: 'success' });
            // Refresh courses to update enrollment status
            fetchCourses();
        } catch (err) {
            console.error('Enrollment failed:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Enrollment failed. Please try again.',
                severity: 'error',
            });
        } finally {
            setEnrollingId(null);
        }
    }, [fetchCourses]);

    const handleCategoryChange = useCallback((cat) => {
        setCategory(cat);
        setPage(1);
    }, []);

    const handleClearFilters = useCallback(() => {
        setSearch('');
        setSearchDebounced('');
        setCategory('All');
        setDifficulty('all');
        setPricing('all');
        setPage(1);
    }, []);

    const hasActiveFilters = category !== 'All' || difficulty !== 'all' || pricing !== 'all' || searchDebounced;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Hero Section */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 4, md: 6 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -60,
                        right: -60,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.08)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -40,
                        left: '30%',
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.05)',
                    }}
                />

                <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontFamily,
                            fontWeight: 800,
                            color: 'white',
                            mb: 1,
                            fontSize: { xs: '1.8rem', md: '2.5rem' },
                        }}
                    >
                        Explore Courses
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily,
                            color: 'rgba(255,255,255,0.85)',
                            mb: 3,
                            fontSize: { xs: '0.9rem', md: '1.05rem' },
                        }}
                    >
                        Discover courses to expand your knowledge and advance your career
                    </Typography>

                    {/* Search bar */}
                    <TextField
                        fullWidth
                        placeholder="Search courses by title, topic, or keyword..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch('')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{
                            maxWidth: 600,
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'white',
                                borderRadius: '14px',
                                fontFamily,
                                fontSize: '0.95rem',
                                '& fieldset': { border: 'none' },
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            },
                        }}
                    />
                </Box>
            </Paper>

            {/* Main Content */}
            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Category Tabs */}
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    {CATEGORY_LIST.map((cat) => (
                        <Chip
                            key={cat}
                            label={cat}
                            clickable
                            onClick={() => handleCategoryChange(cat)}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                bgcolor: category === cat ? ACCENT : 'white',
                                color: category === cat ? 'white' : '#475569',
                                border: category === cat ? 'none' : '1px solid #e2e8f0',
                                '&:hover': {
                                    bgcolor: category === cat ? '#059669' : '#f1f5f9',
                                },
                                transition: 'all 0.2s',
                            }}
                        />
                    ))}
                    <Tooltip title={showFilters ? 'Hide filters' : 'More filters'}>
                        <IconButton
                            onClick={() => setShowFilters(!showFilters)}
                            sx={{
                                color: showFilters ? ACCENT : '#64748b',
                                bgcolor: showFilters ? `${ACCENT}15` : 'transparent',
                            }}
                        >
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>
                    {hasActiveFilters && (
                        <Chip
                            label="Clear all"
                            size="small"
                            onDelete={handleClearFilters}
                            sx={{ fontFamily, fontSize: '0.75rem', color: '#ef4444', borderColor: '#ef4444' }}
                            variant="outlined"
                        />
                    )}
                </Box>

                {/* Extra Filters */}
                {showFilters && (
                    <Fade in={showFilters}>
                        <Paper
                            sx={{
                                p: 2.5,
                                mb: 3,
                                borderRadius: '14px',
                                border: '1px solid #e2e8f0',
                                boxShadow: 'none',
                                display: 'flex',
                                gap: 2,
                                flexWrap: 'wrap',
                                alignItems: 'center',
                            }}
                        >
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel sx={{ fontFamily }}>Difficulty</InputLabel>
                                <Select
                                    value={difficulty}
                                    label="Difficulty"
                                    onChange={(e) => { setDifficulty(e.target.value); setPage(1); }}
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {DIFFICULTY_LIST.map((d) => (
                                        <MenuItem key={d} value={d} sx={{ fontFamily }}>
                                            {d.charAt(0).toUpperCase() + d.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel sx={{ fontFamily }}>Pricing</InputLabel>
                                <Select
                                    value={pricing}
                                    label="Pricing"
                                    onChange={(e) => { setPricing(e.target.value); setPage(1); }}
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {PRICING_LIST.map((p) => (
                                        <MenuItem key={p} value={p} sx={{ fontFamily }}>
                                            {p.charAt(0).toUpperCase() + p.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Paper>
                    </Fade>
                )}

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchCourses} sx={{ fontFamily, textTransform: 'none' }}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading */}
                {loading ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                                <CourseCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                ) : courses.length === 0 ? (
                    /* Empty State */
                    <Paper
                        sx={{
                            py: 8,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <SchoolIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            No courses found
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>
                            {hasActiveFilters
                                ? 'Try adjusting your filters or search terms.'
                                : 'Courses will appear here once they are published.'}
                        </Typography>
                        {hasActiveFilters && (
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderColor: ACCENT,
                                    color: ACCENT,
                                    borderRadius: '10px',
                                }}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </Paper>
                ) : (
                    /* Course Grid */
                    <>
                        <Grid container spacing={3}>
                            {courses.map((course, index) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={course.id || course._id || index}>
                                    <CourseCard
                                        course={course}
                                        index={index}
                                        onEnroll={handleEnroll}
                                        enrollingId={enrollingId}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, value) => setPage(value)}
                                    color="primary"
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontFamily,
                                            fontWeight: 600,
                                            borderRadius: '10px',
                                        },
                                        '& .Mui-selected': {
                                            bgcolor: `${ACCENT} !important`,
                                            color: 'white',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '12px', width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
